import { faSquare } from "@fortawesome/free-regular-svg-icons";
import {
  faArchive,
  faArrowUpRightFromSquare,
  faContactCard,
  faFileExcel,
  faFilePdf,
  faICursor,
  faListDots,
  faPencil,
  faPlus,
  faSquareCheck,
  faUndo,
  faUpload,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useKeycloak } from "@react-keycloak/web";
import { DataTable } from "@ventumsoftware/react-toolkit";
import moment from "moment/moment";
import React, { type FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useApi } from "../api/APIContext";
import { useApiCall } from "../api/api";
import type { Assessor, Company, Exam, ExamParticipant, Project } from "../api/dto";
import type { QuestionSet } from "../api/dto/QuestionSet";
import { ContentContainer } from "../components/content/ContentContainer";
import { HoverHint } from "../components/content/HoverHint";
import { PageHeader } from "../components/content/PageHeader";
import { Badge, BadgeStyle } from "../components/data/Badge";
import { ScoreBadge } from "../components/data/ScoreBadge";
import { Button } from "../components/form/Button";
import { Input } from "../components/form/Input";
import { Select } from "../components/form/Select";
import { SelectBox, type SelectBoxOption, SelectBoxSkeleton } from "../components/form/SelectBox";
import { Toggle } from "../components/form/Toggle";
import { useModal } from "../components/layout/ModalProvider";
import { AddCompanyLogoModal } from "../modals/AddCompanyLogoModal";
import { AddCompanyModal } from "../modals/AddCompanyModal";
import { AddProjectLogoModal } from "../modals/AddProjectLogoModal";
import { AddProjectModal } from "../modals/AddProjectModal";
import { ArchiveCompanyModal } from "../modals/ArchiveCompanyModal";
import { ArchiveProjectModal } from "../modals/ArchiveProjectModal";
import { BulkContractorRenameModal } from "../modals/BulkContractorRenameModal";
import { BulkSubcontractorRenameModal } from "../modals/BulkSubcontractorRenameModal";
import { EditCompanyScoreOverridesModal } from "../modals/EditCompanyScoreOverridesModal";
import { EditProjectContactModal } from "../modals/EditProjectContactModal";
import { EditProjectScoreOverridesModal } from "../modals/EditProjectScoreOverridesModal";
import { ExportFullScoreCardModal } from "../modals/ExportFullScorecardModal";
import { ExportManagementDashboardModal } from "../modals/ExportManagementDashboardModal";
import { ExportManagementDataReportModal } from "../modals/ExportProjectReportModal";
import { RenameCompanyAliasModal } from "../modals/RenameCompanyAliasModal";
import { RenameCompanyModal } from "../modals/RenameCompanyModal";
import { RenameContractorModal } from "../modals/RenameContractorModal";
import { RenameProjectModal } from "../modals/RenameProjectModal";
import { RenameSubContractorModal } from "../modals/RenameSubContractorModal";
import { resolveScoringMap } from "../util/categoryScoring";
import { getOverallDataFileName } from "../util/getOverallDataFileName";

const statusFilters = {
  Alle: (exam: Exam) => exam.archivedAt === null,
  Afgerond: (exam: Exam) => exam.status === "Afgerond",
  "In uitvoering": (exam: Exam) => exam.status === "In uitvoering",
  Concept: (exam: Exam) => exam.status === "Concept",
  Gearchiveerd: (exam: Exam) => exam.status === "Gearchiveerd",
};
const attemptFilters = {
  Alle: (_: ExamParticipant) => true,
  "Eerste poging": (participant: ExamParticipant) =>
    participant.recentAttempts !== null && participant.recentAttempts === 0,
  Herkansingen: (participant: ExamParticipant) =>
    participant.recentAttempts !== null && participant.recentAttempts > 0,
};
const noShowFilters = {
  Alle: (_: ExamParticipant) => true,
  "Enkel No-shows": (participant: ExamParticipant) => participant.noShow,
  "Geen No-shows": (participant: ExamParticipant) => !participant.noShow,
};

interface FilterState {
  companyId: string[];
  projectId: string[];
  examId: string[];
  contractor: string[];
  subContractor: string[];
  includeArchived: boolean;
  fromDate: Date;
  toDate: Date;
  status: keyof typeof statusFilters;
  questionSet: string;
  attempt: keyof typeof attemptFilters;
  noShow: keyof typeof noShowFilters;
  assessor: string;
}

export interface SelectedParticipantRow extends ExamParticipant {
  examName: string;
  examDate: string;
  examTime: string;
  /** questionSetName is the name of the question set of the exam, or "" when unknown. */
  questionSetName: string;
  exam: Exam;
  project: Project;
  company: Company;
  assessor: Assessor | null;
}
export const BrowseAndFilterPage: FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { exams, projects, companies, participants, assessors, questionSets, loading } = useApi();
  const [filters, setFilters] = useState<FilterState>({
    companyId: [],
    projectId: [],
    examId: [],
    contractor: [],
    subContractor: [],
    includeArchived: false,
    fromDate: moment("01-08-2024").toDate(),
    toDate: moment().toDate(),
    status: "Afgerond",
    questionSet: "Alle",
    attempt: "Alle",
    noShow: "Alle",
    assessor: "Alle",
  });
  useEffect(() => {
    // Clean up companies from filter that have just been archived or merged
    const companyIds = companies.map((p) => p.id);
    if (filters.companyId.some((id) => !companyIds.includes(id))) {
      setFilters((old) => ({
        ...old,
        companyId: old.companyId.filter((id) => companyIds.includes(id)),
      }));
    }
    // Clean up projects from filter that have just been archived or merged
    const projectIds = projects.map((p) => p.id);
    if (filters.projectId.some((id) => !projectIds.includes(id))) {
      setFilters((old) => ({
        ...old,
        projectId: old.projectId.filter((id) => projectIds.includes(id)),
      }));
    }
  }, [filters, projects, companies]);

  const filteredCandidates: SelectedParticipantRow[] = useMemo(() => {
    const projectsMap = projects.reduce(
      (map, project) => {
        map[project.id] = project;
        return map;
      },
      {} as { [key: string]: Project },
    );
    const companiesMap = companies.reduce(
      (map, company) => {
        map[company.id] = company;
        return map;
      },
      {} as { [key: string]: Company },
    );
    const examsMap = exams.reduce(
      (map, exam) => {
        map[exam.id] = exam;
        return map;
      },
      {} as { [key: string]: Exam },
    );
    const assessorsMap = assessors.reduce(
      (map, assessor) => {
        map[assessor.id] = assessor;
        return map;
      },
      {} as { [key: string]: Assessor },
    );

    const availableProjectIds = projects
      .filter((project) => filters.projectId.length === 0 || filters.projectId.includes(project.id))
      .filter(
        (project) =>
          filters.companyId.length === 0 || filters.companyId.includes(project.companyId),
      )
      .map((p) => p.id);
    const availableExamIds = exams
      .filter((exam) =>
        moment(exam.examAt).isBetween(
          moment(filters.fromDate),
          moment(filters.toDate),
          "day",
          "[]",
        ),
      )
      .filter((exam) => availableProjectIds.includes(exam.projectId))
      .filter((exam) => filters.status === "Alle" || statusFilters[filters.status](exam))
      .filter((exam) => {
        if (filters.questionSet === "Alle") {
          return true;
        }
        if (filters.questionSet === "Rct") {
          return questionSets
            .filter((qs) => qs.type === "Rct")
            .map((qs) => qs.id)
            .includes(exam.questionSetId);
        }
        if (filters.questionSet === "Fct") {
          return questionSets
            .filter((qs) => qs.type === "Fct")
            .map((qs) => qs.id)
            .includes(exam.questionSetId);
        }
        if (filters.questionSet === "Fir") {
          return questionSets
            .filter((qs) => qs.type === "Fir")
            .map((qs) => qs.id)
            .includes(exam.questionSetId);
        }
        if (filters.questionSet === "Lir") {
          return questionSets
            .filter((qs) => qs.type === "Lir")
            .map((qs) => qs.id)
            .includes(exam.questionSetId);
        }
        return exam.questionSetId === filters.questionSet;
      })
      .filter((exam) => filters.assessor === "Alle" || exam.assessorId === filters.assessor)
      .map((e) => e.id);
    const allParticipants = participants
      .filter((p) => !p.deletedAt)
      .filter((p) =>
        filters.examId.length > 0
          ? filters.examId.includes(p.examId)
          : availableExamIds.includes(p.examId),
      )
      .filter((p) => filters.contractor.length === 0 || filters.contractor.includes(p.contractor))
      .filter(
        (p) =>
          filters.subContractor.length === 0 || filters.subContractor.includes(p.subContractor),
      )
      .filter((p) => attemptFilters[filters.attempt](p))
      .filter((p) => noShowFilters[filters.noShow](p));

    const sortedParticipants = allParticipants.sort((a, b) => {
      const examA = examsMap[a.examId];
      const examB = examsMap[b.examId];
      const nameComparison = (examA.name ?? "").localeCompare(examB.name ?? "");
      if (nameComparison !== 0) return nameComparison;
      return a.position - b.position;
    });

    const returnData = sortedParticipants
      .map((participant) => {
        const exam = examsMap[participant.examId];
        const project = projectsMap[exam.projectId];
        const company = companiesMap[project.companyId];
        const assessor = exam.assessorId ? assessorsMap[exam.assessorId] : null;
        return {
          ...participant,
          examName: exam.name ?? "",
          examDate: moment(exam.examAt).format("YYYY-MM-DD"),
          examTime: moment(exam.examAt).format("HH:mm"),
          questionSetName: questionSets.find((q) => q.id === exam.questionSetId)?.name ?? "",
          exam,
          project,
          company,
          assessor,
        };
      })
      .sort((a, b) => (a?.exam.name || "").localeCompare(b?.exam.name || ""));

    if (search === "") {
      return returnData;
    }

    return returnData.filter((item) => {
      if (item.exam.name?.toLowerCase().includes(search.toLowerCase())) {
        return true;
      }
      if (item.contractor?.toLowerCase().includes(search.toLowerCase())) {
        return true;
      }
      if (item.subContractor?.toLowerCase().includes(search.toLowerCase())) {
        return true;
      }
      if (item.name?.toLowerCase().includes(search.toLowerCase())) {
        return true;
      }
    });
  }, [filters, statusFilters, participants, projects, assessors, exams, questionSets, search]);

  const questionSetsMap = useMemo(
    () =>
      questionSets.reduce(
        (map, questionSet) => {
          map[questionSet.id] = questionSet;
          return map;
        },
        {} as { [key: string]: QuestionSet },
      ),
    [questionSets],
  );
  const scoreCategories = useMemo(
    () =>
      Array.from(new Set(filteredCandidates.flatMap((c) => Object.keys(c.scorePreviewData ?? {})))),
    [filteredCandidates],
  );

  const questionSetFilterOptions = useMemo(() => {
    return Object.fromEntries([
      ["Alle", "Alle"],
      ["Fct", "Fct - Alle"],
      ...questionSets.filter((q) => q.type === "Fct").map((q) => [q.id, `Fct - ${q.name}`]),
      ["Fir", "Fir - Alle"],
      ...questionSets.filter((q) => q.type === "Fir").map((q) => [q.id, `Fir - ${q.name}`]),
      ["Lir", "Lir - Alle"],
      ...questionSets.filter((q) => q.type === "Lir").map((q) => [q.id, `Lir - ${q.name}`]),
      ["Rct", "Rct - Alle"],
      ...questionSets.filter((q) => q.type === "Rct").map((q) => [q.id, `Rct - ${q.name}`]),
    ]);
  }, [questionSets]);
  const setFromDate = (fromDate: Date) => setFilters((old) => ({ ...old, fromDate }));
  const setToDate = (toDate: Date) => setFilters((old) => ({ ...old, toDate }));
  const setIncludeArchived = (includeArchived: boolean) =>
    setFilters((old) => ({ ...old, includeArchived }));
  const assessorFilters = Object.fromEntries([
    ["-", "Alle"],
    ...assessors.map((item) => [item.id, `${item.first_name} ${item.last_name}`]),
  ]);
  const { keycloak } = useKeycloak();
  const roles = useMemo(
    () => keycloak.tokenParsed?.realm_access?.roles ?? [],
    [keycloak.tokenParsed],
  );
  const isCustomer = roles.includes("customer");
  const [selectedParticipants, setSelectedParticipants] = useState<SelectedParticipantRow[]>([]);
  const bulkContractorRenameModal = useModal({
    title: "Contractor hernoemen (Bulk)",
    body: <BulkContractorRenameModal participants={selectedParticipants} />,
    size: "md",
  });
  const bulkSubcontractorRenameModal = useModal({
    title: "Subontractor hernoemen (Bulk)",
    body: <BulkSubcontractorRenameModal participants={selectedParticipants} />,
    size: "md",
  });
  return (
    <ContentContainer size={"2xl"}>
      <PageHeader>Bladeren en Filteren</PageHeader>
      {loading ? (
        <>
          <div className={"bg-white border-2 border-slate-100 mb-4 rounded-lg px-4 py-3"}>
            <FilterSettingsSkeleton />
          </div>
        </>
      ) : (
        <>
          <div className={"bg-white border-2 border-slate-100 mb-4 rounded-lg px-4 py-3"}>
            <FilterSettings
              filters={filters}
              onFilterChange={setFilters}
              exams={exams}
              projects={projects}
              companies={companies}
              participants={participants}
              isCustomer={isCustomer}
            />
            <div
              className={
                "grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-200 -mx-4 px-4"
              }
            >
              <Select
                label={"Examen status"}
                options={Object.fromEntries(Object.keys(statusFilters).map((s) => [s, s]))}
                value={filters.status}
                onChange={(v) =>
                  setFilters((old) => ({ ...old, status: v as keyof typeof statusFilters }))
                }
              />
              <Input
                type={"date"}
                value={filters.fromDate}
                onChange={setFromDate}
                label={"Vanaf (datum)"}
              />
              <Input
                type={"date"}
                value={filters.toDate}
                onChange={setToDate}
                label={"Tot en met (datum)"}
              />
              <Toggle
                value={filters.includeArchived}
                onChange={setIncludeArchived}
                label={"Toon gearchiveerde"}
              />
              <Select
                label={"Vragenlijst"}
                options={questionSetFilterOptions}
                value={filters.questionSet}
                onChange={(v) => setFilters((old) => ({ ...old, questionSet: v }))}
              />
              <Select
                label={"Herkansing"}
                options={Object.fromEntries(Object.keys(attemptFilters).map((k) => [k, k]))}
                value={filters.attempt}
                onChange={(v) =>
                  setFilters((old) => ({ ...old, attempt: v as keyof typeof attemptFilters }))
                }
              />
              <Select
                label={"No-Shows"}
                options={Object.fromEntries(Object.keys(noShowFilters).map((k) => [k, k]))}
                value={filters.noShow}
                onChange={(v) =>
                  setFilters((old) => ({ ...old, noShow: v as keyof typeof noShowFilters }))
                }
              />
              <Select
                label={"Assessor"}
                options={assessorFilters}
                value={filters.assessor}
                onChange={(v) => setFilters((old) => ({ ...old, assessor: v }))}
              />
              <div
                className={`col-span-4 flex flex-col justify-between text-sm font-medium text-brand-900 w-full`}
              >
                <div>Snelle opties</div>
                <div className={"flex justify-between"}>
                  <Button
                    type={"secondary"}
                    size={"md"}
                    text={"deze maand"}
                    onClick={() => {
                      setFromDate(moment().startOf("month").toDate());
                      setToDate(moment().endOf("month").toDate());
                    }}
                  />
                  <Button
                    type={"secondary"}
                    size={"md"}
                    text={"vorige maand"}
                    onClick={() => {
                      setFromDate(moment().subtract(1, "month").startOf("month").toDate());
                      setToDate(moment().subtract(1, "month").endOf("month").toDate());
                    }}
                  />
                  <Button
                    type={"secondary"}
                    size={"md"}
                    text={"deze week"}
                    onClick={() => {
                      setFromDate(moment().startOf("week").toDate());
                      setToDate(moment().endOf("week").toDate());
                    }}
                  />
                  <Button
                    type={"secondary"}
                    size={"md"}
                    text={"laatste 90 dagen"}
                    onClick={() => {
                      setFromDate(moment().subtract(90, "days").toDate());
                      setToDate(moment().toDate());
                    }}
                  />
                  <Button
                    type={"secondary"}
                    size={"md"}
                    text={"Alle"}
                    onClick={() => {
                      setFromDate(moment().subtract(2, "years").toDate());
                      setToDate(moment().add(100, "years").toDate());
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <ExportCard
            allParticipants={participants}
            filteredParticipants={filteredCandidates}
            filters={filters}
            allCompanies={companies}
            allProjects={projects}
            allExams={exams}
            isCustomer={isCustomer}
          />
          <div className={"flex flex-col mb-4"}>
            <div className={"flex flex-1"}>
              <Input
                type={"text"}
                label={"Zoeken"}
                value={search}
                onChange={setSearch}
                placeholder={"Zoek op examen, (sub)contractor of naam..."}
              />
            </div>
            {selectedParticipants.length > 0 && (
              <div
                className={`flex-col justify-end items-center border-2 border-dashed border-brand-400 rounded-sm bg-brand-50 px-4 py-3 mt-4`}
              >
                <div className={"h-8 mb-1 flex items-center space-x-3"}>
                  <div className={"flex-1"}>
                    Met geselecteerde (
                    <span className={"font-medium"}>{selectedParticipants.length}</span>):{" "}
                  </div>
                  <Button
                    type={"secondary"}
                    size={"sm"}
                    text={"Selecteer alles"}
                    icon={faSquareCheck}
                    onClick={() => setSelectedParticipants(filteredCandidates)}
                  />
                  <Button
                    type={"secondary"}
                    size={"sm"}
                    text={"Selectie opheffen"}
                    icon={faSquare}
                    onClick={() => setSelectedParticipants([])}
                  />
                </div>
                <div className={"h-8 flex items-center space-x-3"}>
                  <Button
                    type={"primary"}
                    size={"sm"}
                    text={"Contractor aanpassen"}
                    icon={faPencil}
                    onClick={() => bulkContractorRenameModal.open()}
                  />
                  <Button
                    type={"primary"}
                    size={"sm"}
                    text={"Subcontractor aanpassen"}
                    icon={faPencil}
                    onClick={() => bulkSubcontractorRenameModal.open()}
                  />
                </div>
              </div>
            )}
          </div>
          {filteredCandidates.length === 0 ? (
            <div className={"flex flex-col items-center py-8"}>
              <div>Geen resultaten gevonden voor filter instellingen.</div>
              <div>
                <Button
                  type={"secondary"}
                  size={"sm"}
                  text={"Reset filters"}
                  icon={faUndo}
                  onClick={() => {
                    setFilters({
                      companyId: [],
                      projectId: [],
                      examId: [],
                      contractor: [],
                      subContractor: [],
                      includeArchived: false,
                      fromDate: moment().subtract(90, "days").toDate(),
                      toDate: moment().toDate(),
                      status: "Afgerond",
                      questionSet: "Alle",
                      attempt: "Alle",
                      noShow: "Alle",
                      assessor: "Alle",
                    });
                  }}
                />
              </div>
            </div>
          ) : (
            <DataTable
              keyProperty={"id"}
              data={filteredCandidates}
              compact
              pageSize={100}
              columns={[
                {
                  header: "",
                  sort: false,
                  property: "id",
                  transform: (_, row) => {
                    const selectedParticipantRow = selectedParticipants.find(
                      (sp) => sp.id === row.id,
                    );
                    return (
                      <button
                        type={"button"}
                        aria-label={selectedParticipantRow ? "Deselecteer rij" : "Selecteer rij"}
                        aria-pressed={!!selectedParticipantRow}
                        className={
                          "flex h-8 w-8 items-center justify-center rounded-sm cursor-pointer hover:bg-brand-100"
                        }
                        onClick={() => {
                          if (selectedParticipantRow) {
                            setSelectedParticipants((old) => [
                              ...old.filter((sp) => sp.id !== row.id),
                            ]);
                          } else {
                            setSelectedParticipants((old) => [...old, row]);
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={selectedParticipantRow ? faSquareCheck : faSquare} />
                      </button>
                    );
                  },
                },
                {
                  header: "Examen",
                  sort: true,
                  property: "examName",
                  transform: (_, row) => (
                    <div className={"flex items-center whitespace-nowrap"}>
                      {row.examName ?? "-"}{" "}
                      {isCustomer ? undefined : (
                        <button
                          className={
                            "text-brand-700 ml-1 px-1 rounded-sm hover:bg-brand-100 cursor-pointer"
                          }
                          onClick={() => navigate(`/exams/${row.examId}`)}
                        >
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                        </button>
                      )}{" "}
                    </div>
                  ),
                },
                {
                  header: "Vragenlijst",
                  sort: true,
                  property: "questionSetName",
                  transform: (_, row) =>
                    row.questionSetName.length > 10 ? (
                      <HoverHint hint={row.questionSetName}>
                        {`${row.questionSetName.slice(0, 10)}…`}
                      </HoverHint>
                    ) : (
                      row.questionSetName
                    ),
                },
                {
                  header: "Dag",
                  sort: true,
                  property: "examDate",
                },
                {
                  header: "Tijd",
                  sort: true,
                  property: "examTime",
                },
                {
                  header: "Contractor",
                  sort: true,
                  property: "contractor",
                  transform: (_, row) => <div className={"w-48"}>{row.contractor}</div>,
                },
                {
                  header: "Subcontractor",
                  sort: true,
                  property: "subContractor",
                  transform: (_, row) => <div className={"w-36"}>{row.subContractor ?? " "}</div>,
                },
                {
                  header: "Naam",
                  sort: true,
                  property: "name",
                  transform: (_, row) => (
                    <div className={"w-36"}>
                      {row.name}
                      {row.recentAttempts !== null && row.recentAttempts > 0 ? (
                        <div
                          className={
                            "bg-red-700 text-white rounded-full inline-flex items-center align-center justify-center h-4 w-4 ml-2 text-xs font-bold"
                          }
                        >
                          {row.recentAttempts + 1}
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  ),
                },
                ...scoreCategories.map((category) => ({
                  header: category,
                  sort: false,
                  property: "score" as const,
                  transform: (_: string | null, row: SelectedParticipantRow) => {
                    if (row.noShow) {
                      return <Badge style={BadgeStyle.info}>No-show</Badge>;
                    }
                    if (row.isAborted) {
                      return <Badge style={BadgeStyle.dark}>ABORT</Badge>;
                    }
                    const score = row.scorePreviewData?.[category];
                    if (score === undefined) return <></>;
                    const scoringMap = resolveScoringMap(
                      row.exam,
                      row.project,
                      row.company,
                      questionSetsMap[row.exam.questionSetId],
                    );
                    return <ScoreBadge scoringMap={scoringMap} category={category} score={score} />;
                  },
                })),
                {
                  header: "Assessor",
                  sort: true,
                  property: "assessor",
                  transform: (_, row) =>
                    `${row.assessor?.first_name ?? ""} ${row.assessor?.last_name ?? ""}`.trim(),
                },
              ]}
            />
          )}
        </>
      )}
    </ContentContainer>
  );
};

const FilterSettings: FC<{
  filters: FilterState;
  onFilterChange: (val: FilterState) => void;
  exams: Exam[];
  projects: Project[];
  companies: Company[];
  participants: ExamParticipant[];
  isCustomer: boolean;
}> = (props) => {
  const { questionSets } = useApi();
  const companyOptions = useMemo(() => {
    return props.companies
      .filter((company) => (props.filters.includeArchived ? true : !company.isArchived))
      .map((company) => ({
        value: company.id,
        label: company.isArchived ? `[Archief] ${company.name}` : company.name,
        leading: (
          <div className={"h-6 w-6 rounded-sm bg-slate-100 overflow-hidden ml-3 -mr-1"}>
            {company.logo ? (
              <img src={company.logo} alt={company.name} className={"h-full w-full object-cover"} />
            ) : (
              <></>
            )}
          </div>
        ),
      }));
  }, [props.companies, props.filters.includeArchived]);

  const projectOptions = useMemo(() => {
    return props.projects
      .filter(
        (project) =>
          props.filters.companyId.length === 0 ||
          props.filters.companyId.includes(project.companyId),
      )
      .filter((project) => (props.filters.includeArchived ? true : !project.isArchived))
      .map((project) => ({
        value: project.id,
        label: project.isArchived ? `[Archief] ${project.name}` : project.name,
        leading: (
          <div className={"h-6 w-6 rounded-sm bg-slate-100 overflow-hidden ml-3 -mr-1"}>
            {project.logo ? (
              <img src={project.logo} alt={project.name} className={"h-full w-full object-cover"} />
            ) : (
              <></>
            )}
          </div>
        ),
      }));
  }, [props.projects, props.filters.companyId, props.filters.includeArchived]);

  const examOptions = useMemo(() => {
    const projectOptionsIds: string[] = projectOptions.map((p: SelectBoxOption) => p.value);
    return props.exams
      .filter((exam) =>
        moment(exam.examAt).isBetween(
          moment(props.filters.fromDate),
          moment(props.filters.toDate),
          "day",
          "[]",
        ),
      )
      .filter(
        (exam) =>
          (props.filters.projectId.length === 0 && projectOptionsIds.includes(exam.projectId)) ||
          props.filters.projectId.includes(exam.projectId),
      )
      .filter((exam) => (props.filters.includeArchived ? true : !exam.archivedAt))
      .map((exam) => ({
        value: exam.id,
        label: exam.archivedAt ? `[Archief] ${exam.name}` : (exam.name ?? "Onbekend examen"),
      }));
  }, [props.exams, props.filters.projectId, projectOptions]);

  const contractorOptions = useMemo(() => {
    const examOptionsIds: string[] = examOptions.map((e: SelectBoxOption) => e.value);
    const participants = props.participants.filter((participant) =>
      props.filters.examId.length > 0
        ? props.filters.examId.includes(participant.examId)
        : examOptionsIds.includes(participant.examId),
    );
    const contractors = Array.from(
      new Set(participants.map((participant) => participant.contractor)),
    );
    return contractors
      .map((contractor) => ({ value: contractor, label: contractor }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    props.participants,
    props.filters.examId,
    props.filters.fromDate,
    props.filters.toDate,
    examOptions,
  ]);

  const subContractorOptions = useMemo(() => {
    const examOptionsIds: string[] = examOptions.map((e: SelectBoxOption) => e.value);
    const participants = props.participants.filter((participant) => {
      if (props.filters.contractor.length > 0) {
        return (
          props.filters.contractor.includes(participant.contractor) &&
          examOptionsIds.includes(participant.examId)
        );
      }
      return examOptionsIds.includes(participant.examId);
    });
    const subContractors = Array.from(
      new Set(participants.map((participant) => participant.subContractor).filter((x) => !!x)),
    );
    return subContractors
      .map((subContractor) => ({ value: subContractor, label: subContractor }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [props.participants, examOptions, props.filters.contractor]);

  const addCompany = useModal({ title: "Voeg Bedrijf toe", body: <AddCompanyModal />, size: "lg" });
  const addProject = useModal({ title: "Voeg Project toe", body: <AddProjectModal />, size: "lg" });
  return (
    <div className={"grid grid-cols-3 xl:grid-cols-5 gap-4 text-slate-800 text-sm"}>
      <div>
        <div className={"flex justify-between items-center"}>
          <strong className={"mb-2 block"}>Bedrijf</strong>
          {props.isCustomer ? (
            <div className={"pb-[38px]"}></div>
          ) : (
            <FontAwesomeIcon
              icon={faPlus}
              className={"py-2 px-2 mb-2 hover:bg-brand-100 rounded-sm hover:cursor-pointer"}
              onClick={() => addCompany.open()}
            />
          )}
        </div>
        <SelectBox
          mode={"multi"}
          options={companyOptions}
          value={props.filters.companyId}
          onChange={(companyIds) => {
            const projectIdsOfCompanyIds = props.projects
              .filter((p) => companyIds.includes(p.companyId))
              .map((p) => p.id);
            props.onFilterChange({
              ...props.filters,
              companyId: companyIds,
              projectId: props.filters.projectId.filter((projectId) =>
                projectIdsOfCompanyIds.includes(projectId),
              ),
              examId: [],
              contractor: [],
              subContractor: [],
            });
          }}
          contextMenuBuilder={
            props.isCustomer
              ? undefined
              : (option, close) => (
                  <CompanyContextMenu
                    company={props.companies.find((x) => x.id === option.value)}
                    questionSets={questionSets}
                    companies={props.companies}
                    close={close}
                  />
                )
          }
        />
      </div>
      <div>
        <div className={"flex justify-between items-center"}>
          <strong className={"mb-2 block"}>Project</strong>
          {props.isCustomer ? (
            <div className={"pb-[38px]"}></div>
          ) : (
            <FontAwesomeIcon
              icon={faPlus}
              className={"py-2 px-2 mb-2 hover:bg-brand-100 rounded-sm hover:cursor-pointer"}
              onClick={() => addProject.open()}
            />
          )}
        </div>
        <SelectBox
          mode={"multi"}
          options={projectOptions}
          value={props.filters.projectId}
          onChange={(projectIds) => {
            const companyIdsOfProjects = props.projects
              .filter((p) => projectIds.includes(p.id))
              .map((p) => p.companyId);
            const newCompanyIds = Array.from(
              new Set([...props.filters.companyId, ...companyIdsOfProjects]),
            );
            props.onFilterChange({
              ...props.filters,
              companyId: newCompanyIds,
              projectId: projectIds,
              examId: [],
              contractor: [],
              subContractor: [],
            });
          }}
          contextMenuBuilder={
            props.isCustomer
              ? undefined
              : (option, close) => (
                  <ProjectContextMenu
                    project={props.projects.find((x) => x.id === option.value)}
                    questionSets={questionSets}
                    projects={props.projects}
                    close={close}
                  />
                )
          }
        />
      </div>
      <div>
        <strong className={"mb-2 py-1 block"}>
          Examenmoment <span className={"text-slate-400"}>(optioneel)</span>
        </strong>
        <SelectBox
          mode={"multi"}
          options={examOptions}
          value={props.filters.examId}
          onChange={(examId) => props.onFilterChange({ ...props.filters, examId })}
        />
      </div>
      <div>
        <strong className={"mb-2 py-1 block"}>Contractor</strong>
        <SelectBox
          mode={"multi"}
          options={contractorOptions}
          value={props.filters.contractor}
          onChange={(contractor) =>
            props.onFilterChange({ ...props.filters, contractor, subContractor: [] })
          }
          contextMenuBuilder={
            props.isCustomer
              ? undefined
              : props.filters.companyId.length === 1
                ? (option, close) => (
                    <ProjectContractorMenu
                      currentContractor={option.label}
                      participants={props.participants}
                      companyId={props.filters.companyId[0]}
                      exams={props.exams.filter((e) => e.companyId === props.filters.companyId[0])}
                      close={close}
                    />
                  )
                : undefined
          }
        />
      </div>
      <div>
        <strong className={"mb-2 py-1 block"}>Subcontractor</strong>
        <SelectBox
          mode={"multi"}
          options={subContractorOptions}
          value={props.filters.subContractor}
          onChange={(subContractor) => props.onFilterChange({ ...props.filters, subContractor })}
          contextMenuBuilder={
            props.isCustomer
              ? undefined
              : props.filters.companyId.length === 1
                ? (option, close) => (
                    <SubContractorMenu
                      currentSubContractor={option.label}
                      participants={props.participants}
                      companyId={props.filters.companyId[0]}
                      exams={props.exams.filter((e) => e.companyId === props.filters.companyId[0])}
                      close={close}
                    />
                  )
                : undefined
          }
        />
      </div>
    </div>
  );
};

const CompanyContextMenu: FC<{
  company?: Company;
  companies: Company[];
  questionSets: QuestionSet[];
  close: () => void;
}> = (props) => {
  const renameModal = useModal({
    title: "Bedrijf hernoemen",
    body: <RenameCompanyModal companies={props.companies} company={props.company!} />,
  });
  const editAliasModal = useModal({
    title: "Bedrijf hernoemen",
    body: <RenameCompanyAliasModal company={props.company!} />,
  });
  const editModelModal = useModal({
    title: "Bedrijf scoremodel aanpassen",
    body: (
      <EditCompanyScoreOverridesModal questionSets={props.questionSets} company={props.company!} />
    ),
  });
  const archiveModal = useModal({
    title: "Bedrijf archiveren",
    body: <ArchiveCompanyModal company={props.company!} />,
  });
  const logoModal = useModal({
    title: "Logo uploaden",
    body: <AddCompanyLogoModal company={props.company!} />,
  });
  if (props.company === undefined) {
    return <>Error</>;
  }
  return (
    <div className={"text-left bg-slate-200"}>
      <div className={"px-1 py-1 font-medium text-xs  text-slate-500"}>Opties</div>
      <div className={"bg-white rounded-sm"}>
        <ContextMenuOption
          icon={faICursor}
          text={"Hernoemen"}
          close={props.close}
          onClick={() => renameModal.open()}
        />
        <ContextMenuOption
          icon={faICursor}
          text={"Alias bewerken"}
          close={props.close}
          onClick={() => editAliasModal.open()}
        />
        <ContextMenuOption
          icon={faUpload}
          text={"Logo uploaden"}
          close={props.close}
          onClick={() => logoModal.open()}
        />
        <ContextMenuOption
          icon={faListDots}
          text={"Scoremodel"}
          close={props.close}
          onClick={() => editModelModal.open()}
        />
        <ContextMenuOption
          icon={props.company.isArchived ? faUndo : faArchive}
          text={props.company.isArchived ? "Terugzetten" : "Archiveren"}
          close={props.close}
          onClick={() => archiveModal.open()}
        />
      </div>
    </div>
  );
};

const ProjectContextMenu: FC<{
  project?: Project;
  projects: Project[];
  questionSets: QuestionSet[];
  close: () => void;
}> = (props) => {
  const renameModal = useModal({
    title: "Project hernoemen",
    body: <RenameProjectModal projects={props.projects} project={props.project!} />,
  });
  const editModelModal = useModal({
    title: "Project scoremodel aanpassen",
    size: "lg",
    body: (
      <EditProjectScoreOverridesModal questionSets={props.questionSets} project={props.project!} />
    ),
  });
  const archiveModal = useModal({
    title: "Project archiveren",
    body: <ArchiveProjectModal project={props.project!} />,
  });
  const logoModal = useModal({
    title: "Logo uploaden",
    body: <AddProjectLogoModal project={props.project!} />,
  });
  const contactModal = useModal({
    title: "Contactpersoon instellen",
    body: <EditProjectContactModal project={props.project!} />,
  });

  if (props.project === undefined) {
    return <>Error</>;
  }
  return (
    <div className={"text-left bg-slate-200"}>
      <div className={"px-1 py-1 font-medium text-xs  text-slate-500"}>Opties</div>
      <div className={"bg-white rounded-sm"}>
        <ContextMenuOption
          icon={faICursor}
          text={"Hernoemen"}
          close={props.close}
          onClick={() => renameModal.open()}
        />
        <ContextMenuOption
          icon={faUpload}
          text={"Logo uploaden"}
          close={props.close}
          onClick={() => logoModal.open()}
        />
        <ContextMenuOption
          icon={faContactCard}
          text={"Contactpersoon"}
          close={props.close}
          onClick={() => contactModal.open()}
        />
        <ContextMenuOption
          icon={faListDots}
          text={"Scoremodel"}
          close={props.close}
          onClick={() => editModelModal.open()}
        />
        <ContextMenuOption
          icon={props.project.isArchived ? faUndo : faArchive}
          text={props.project.isArchived ? "Terugzetten" : "Archiveren"}
          close={props.close}
          onClick={() => archiveModal.open()}
        />
      </div>
    </div>
  );
};

const ProjectContractorMenu: FC<{
  currentContractor: string;
  participants: ExamParticipant[];
  companyId: string;
  exams: Exam[];
  close: () => void;
}> = (props) => {
  const renameModal = useModal({
    title: "Contractor hernoemen",
    body: (
      <RenameContractorModal
        contractor={props.currentContractor}
        participants={props.participants}
        companyId={props.companyId}
        exams={props.exams}
      />
    ),
  });
  return (
    <div className={"text-left bg-slate-200"}>
      <div className={"px-1 py-1 font-medium text-xs  text-slate-500"}>Opties</div>
      <div className={"bg-white rounded-sm"}>
        <ContextMenuOption
          icon={faICursor}
          text={"Hernoemen"}
          close={props.close}
          onClick={() => renameModal.open()}
        />
      </div>
    </div>
  );
};

const SubContractorMenu: FC<{
  currentSubContractor: string;
  participants: ExamParticipant[];
  companyId: string;
  exams: Exam[];
  close: () => void;
}> = (props) => {
  const renameModal = useModal({
    title: "SubContractor hernoemen",
    body: (
      <RenameSubContractorModal
        subcontractor={props.currentSubContractor}
        participants={props.participants}
        companyId={props.companyId}
        exams={props.exams}
      />
    ),
  });
  return (
    <div className={"text-left bg-slate-200"}>
      <div className={"px-1 py-1 font-medium text-xs  text-slate-500"}>Opties</div>
      <div className={"bg-white rounded-sm"}>
        <ContextMenuOption
          icon={faICursor}
          text={"Hernoemen"}
          close={props.close}
          onClick={() => renameModal.open()}
        />
      </div>
    </div>
  );
};

const ContextMenuOption: FC<{
  icon: IconDefinition;
  text: string;
  close: () => void;
  onClick: () => void;
}> = (props) => {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        props.onClick();
        props.close();
      }}
    >
      <div
        className={
          "flex items-center h-8 px-1 text-brand-800 rounded-sm font-medium hover:bg-brand-50"
        }
      >
        <FontAwesomeIcon icon={props.icon} className={"mr-1 w-4"} />
        {props.text}
      </div>
    </div>
  );
};

const FilterSettingsSkeleton: FC = () => {
  return (
    <div className={"grid grid-cols-3 xl:grid-cols-5 gap-4 text-slate-800 text-sm"}>
      <div>
        <strong className={"mb-2 block"}>Bedrijf</strong>
        <SelectBoxSkeleton />
      </div>
      <div>
        <strong className={"mb-2 block"}>Project</strong>
        <SelectBoxSkeleton />
      </div>
      <div>
        <strong className={"mb-2 block"}>
          Examenmoment <span className={"text-slate-400"}>(optioneel)</span>
        </strong>
        <SelectBoxSkeleton />
      </div>
      <div>
        <strong className={"mb-2 block"}>Contractor</strong>
        <SelectBoxSkeleton />
      </div>
      <div>
        <strong className={"mb-2 block"}>Subcontractor</strong>
        <SelectBoxSkeleton />
      </div>
    </div>
  );
};

export const ExportCard: FC<{
  allParticipants: ExamParticipant[];
  filteredParticipants: SelectedParticipantRow[];
  filters: FilterState;
  allCompanies: Company[];
  allProjects: Project[];
  allExams: Exam[];
  isCustomer: boolean;
}> = (props) => {
  const { exportSelectionExcelTestReport, exportSelectionCertificatesPdf } = useApiCall();
  const exams = new Set(props.filteredParticipants.map((p) => p.examId));
  const contractors = new Set(props.filteredParticipants.map((p) => p.contractor));
  const allContractors = new Set(props.allParticipants.map((p) => p.contractor));
  const subContractors = new Set(props.filteredParticipants.map((p) => p.subContractor));
  const exportDashboardModal = useModal({
    title: "Management Dashboard Exporteren",
    size: "lg",
    body: (
      <ExportManagementDashboardModal
        company={props.filteredParticipants[0]?.company}
        allCompanies={props.allCompanies}
        allProjects={props.allProjects}
        allExams={props.allExams}
        allParticipants={props.filteredParticipants}
        onlyExams={Array.from(exams).filter((x) => !!x)}
        onlyContractors={
          props.filters.contractor.length === 0 ? null : Array.from(contractors).filter((x) => !!x)
        }
        allContractors={Array.from(allContractors)}
        onlySubContractors={
          props.filters.subContractor.length === 0
            ? null
            : Array.from(subContractors).filter((x) => !!x)
        }
        onlyParticipants={props.filteredParticipants.map((p) => p.id)}
      />
    ),
  });
  const exportManagementDataReport = useModal({
    title: "Management Data Rapport Exporteren",
    size: "2xl",
    body: (
      <ExportManagementDataReportModal
        company={props.filteredParticipants[0]?.company}
        allCompanies={props.allCompanies}
        allProjects={props.allProjects}
        allExams={props.allExams}
        allParticipants={props.filteredParticipants}
        onlyExams={Array.from(exams).filter((x) => !!x)}
        onlyContractors={
          props.filters.contractor.length === 0 ? null : Array.from(contractors).filter((x) => !!x)
        }
        allContractors={Array.from(allContractors)}
        onlySubContractors={
          props.filters.subContractor.length === 0
            ? null
            : Array.from(subContractors).filter((x) => !!x)
        }
        onlyParticipants={props.filteredParticipants.map((p) => p.id)}
      />
    ),
  });
  const exportFullExcelModal = useModal({
    title: "Scorecard Excel Exporteren",
    size: "lg",
    body: (
      <ExportFullScoreCardModal
        company={props.filteredParticipants[0]?.company}
        allCompanies={props.allCompanies}
        allProjects={props.allProjects}
        allExams={props.allExams}
        allParticipants={props.filteredParticipants}
        onlyExams={Array.from(exams).filter((x) => !!x)}
        onlyContractors={
          props.filters.contractor.length === 0 ? null : Array.from(contractors).filter((x) => !!x)
        }
        onlySubContractors={
          props.filters.subContractor.length === 0
            ? null
            : Array.from(subContractors).filter((x) => !!x)
        }
        onlyParticipants={props.filteredParticipants.map((p) => p.id)}
      />
    ),
  });

  if (props.filteredParticipants.length === 0) {
    return (
      <div
        className={
          "bg-amber-100 border-2 border-amber-200 text-amber-900 mb-4 rounded-lg px-4 py-3"
        }
      >
        <h2 className={"font-bold"}>Exporteren niet mogelijk</h2>
        Geen resultaten om te exporteren.
      </div>
    );
  }
  const nQuestionSets = new Set(props.filteredParticipants.map((p) => p.exam.questionSetId)).size;
  if (nQuestionSets > 1) {
    return (
      <div
        className={
          "bg-amber-100 border-2 border-amber-200 text-amber-900 mb-4 rounded-lg px-4 py-3"
        }
      >
        <h2 className={"font-bold"}>Exporteren niet mogelijk</h2>
        De examens in de selectie gebruiken verschillende vragenlijsten.
      </div>
    );
  }
  const nCompanies = new Set(props.filteredParticipants.map((p) => p.exam.companyId)).size;
  if (nCompanies > 1) {
    return (
      <div
        className={
          "bg-amber-100 border-2 border-amber-200 text-amber-900 mb-4 rounded-lg px-4 py-3"
        }
      >
        <h2 className={"font-bold"}>Exporteren niet mogelijk</h2>
        De examens in de selectie vallen onder verschillende bedrijven.
      </div>
    );
  }
  const nParticipants = props.filteredParticipants.filter((p) => !p.noShow).length;
  const nParticipantsOriginalTry = props.filteredParticipants.filter(
    (p) => !p.noShow && p.recentAttempts === 0,
  ).length;
  const nParticipantsFirstRetry = props.filteredParticipants.filter(
    (p) => !p.noShow && p.recentAttempts === 1,
  ).length;
  const nParticipantsSecondRetry = props.filteredParticipants.filter(
    (p) => !p.noShow && p.recentAttempts === 2,
  ).length;
  const nParticipantsNoShow = props.filteredParticipants.filter((p) => p.noShow).length;
  return (
    <div className={"bg-white border-2 border-slate-100 mb-4 rounded-lg px-4 py-3"}>
      <h2 className={"font-bold mb-3"}>
        Resultaten van {nParticipants} kandidaten exporteren{" "}
        <span className={"text-slate-500"}>
          (verdeeld over {exams.size} {exams.size === 1 ? "examen" : "examens"}, exclusief no-shows)
        </span>
      </h2>
      <div
        className={
          "-mt-2 mb-4 text-slate-600 px-3 gap-4 rounded-sm grid grid-cols-4 border border-slate-200"
        }
      >
        <div className={"py-2 border-r border-slate-200"}>
          Eerste test: <strong>{nParticipantsOriginalTry}</strong>
        </div>
        <div className={"py-2 border-r border-slate-200 flex items-center"}>
          <div
            className={
              "bg-red-700 text-white rounded-full inline-flex items-center align-center justify-center mr-2 h-4 w-4 text-xs font-bold"
            }
          >
            {2}
          </div>{" "}
          Eerste herkansing: <strong className={"ml-1"}>{nParticipantsFirstRetry}</strong>
        </div>
        <div className={"py-2 border-r border-slate-200 text-amber-800 flex items-center"}>
          <div
            className={
              "bg-red-700 text-white rounded-full inline-flex items-center align-center justify-center mr-2 h-4 w-4 text-xs font-bold"
            }
          >
            {3}
          </div>{" "}
          Tweede herkansing: <strong className={"ml-1"}>{nParticipantsSecondRetry}</strong>
        </div>
        <div className={"py-2 text-red-800"}>
          No-show: <strong>{nParticipantsNoShow}</strong>
        </div>
      </div>
      <div className={"flex flex-wrap -mb-3"}>
        <div className={"mb-3 mr-3"}>
          <Button
            type={"primary"}
            size={"md"}
            text={"Resultaten exporteren (Xlsx)"}
            icon={faFileExcel}
            onClick={async () => {
              const blob = await exportSelectionExcelTestReport(
                Array.from(exams),
                props.filters.contractor.length > 0 ? props.filters.contractor : null,
                props.filters.subContractor.length > 0 ? props.filters.subContractor : null,
                props.filteredParticipants.map((p) => p.id),
              );
              const file = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = file;
              const project = props.filteredParticipants[0].project;
              const company = props.filteredParticipants[0].company;
              a.download = getOverallDataFileName(props.filteredParticipants, company, project);
              a.click();
              URL.revokeObjectURL(file);
            }}
          />
        </div>

        {props.isCustomer === false && (
          <>
            <div className={"mb-3 mr-3"}>
              <Button
                type={"primary"}
                size={"md"}
                text={"Management Dashboard Exporteren (PDF)"}
                icon={faFilePdf}
                onClick={() => exportDashboardModal.open()}
              />
            </div>
            <div className={"mb-3 mr-3"}>
              <Button
                type={"primary"}
                size={"md"}
                text={"Management Data Rapport Exporteren (PDF)"}
                icon={faFilePdf}
                onClick={() => exportManagementDataReport.open()}
              />
            </div>
            <div className={"mb-3 mr-3"}>
              <Button
                type={"primary"}
                size={"md"}
                text={"Scorecard exporteren (Excel)"}
                icon={faFileExcel}
                onClick={() => exportFullExcelModal.open()}
              />
            </div>
            <div className={"mb-3 mr-3"}>
              <Button
                type={"primary"}
                size={"md"}
                text={"Certificaten exporteren (PDF)"}
                icon={faFilePdf}
                onClick={async () => {
                  const blob = await exportSelectionCertificatesPdf(
                    Array.from(exams).filter((x) => !!x),
                    Array.from(props.filteredParticipants.map((p) => p.id)),
                  );
                  const file = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = file;
                  a.download = "certificates.zip";
                  a.click();
                  URL.revokeObjectURL(file);
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
